import Vue from 'vue';

// Each preference keeps its own local copy and owner marker. Cloud writes are
// serialized; a newer slider value replaces the pending value, never the owner.
export function createCloudPreference<T>(kind: 'equalizer' | 'wallpaper', readLocal: () => T, apply: (value: T) => void, validate: (value: unknown) => T) {
  const marker = `simple-music.${kind}.cloud-owner.v1`;
  const state = Vue.observable({ storage: 'local' as 'local' | 'cloud', busy: false, error: '' });
  let owner = '', generation = 0, committed = readLocal(), pending: T | null = null, writing = false;
  async function request(method: 'GET' | 'PUT', value?: T) {
    const [{ runtime }, { siteAccount }] = await Promise.all([import('./bridge'), import('./site-account')]);
    const id = siteAccount.user?.id, revision = siteAccount.revision, csrf = siteAccount.csrfToken;
    if (!id || !csrf) throw new Error('请先登录云端账号');
    const result = await runtime.api(`/api/site-account/preferences/${kind}`, { method, credentials: 'same-origin', headers: { 'x-csrf-token': csrf, ...(method === 'PUT' ? { 'content-type': 'application/json' } : {}) }, ...(method === 'PUT' ? { body: JSON.stringify({ value }) } : {}), signal: AbortSignal.timeout(20000) });
    if (siteAccount.user?.id !== id || siteAccount.revision !== revision || siteAccount.csrfToken !== csrf) throw new DOMException('云端账号已切换', 'AbortError');
    return result;
  }
  async function select(storage: 'local' | 'cloud') {
    if (state.busy || storage === state.storage) return;
    const run = ++generation; state.busy = true; pending = null;
    try {
      if (storage === 'local') { localStorage.removeItem(marker); owner = ''; state.storage = 'local'; committed = readLocal(); apply(committed); }
      else {
        const { siteAccount, restoreSiteAccount } = await import('./site-account'); await restoreSiteAccount();
        const id = siteAccount.user?.id; if (!id) throw new Error('请先登录云端账号');
        const result = await request('GET'); const value = result.value ? validate(result.value) : validate(readLocal());
        if (!result.value) await request('PUT', value);
        if (run !== generation || siteAccount.user?.id !== id) return;
        localStorage.setItem(marker, id); owner = id; committed = value; state.storage = 'cloud'; apply(value);
      }
      state.error = '';
    } catch (error) { if (run === generation) state.error = (error as Error).message || '云端设置保存失败'; }
    finally { if (run === generation) state.busy = false; }
  }
  async function drain() {
    if (writing) return; writing = true; const run = generation, id = owner;
    try {
      while (pending && run === generation && state.storage === 'cloud') {
        const value = pending; pending = null;
        try { await request('PUT', value); if (run === generation && owner === id) { committed = value; state.error = ''; } }
        catch (error) { if (run === generation) { pending = null; apply(committed); state.error = (error as Error).message || '云端设置保存失败'; } break; }
      }
    } finally { writing = false; if (pending && state.storage === 'cloud') void drain(); }
  }
  function changed(value: T) { if (state.storage === 'cloud') { pending = validate(value); void drain(); } }
  void import('./site-account').then(({ siteAccount, restoreSiteAccount }) => {
    let restoring = false;
    new Vue({ computed: { identity() { return `${siteAccount.ready}:${siteAccount.user?.id || ''}:${siteAccount.revision}`; } }, watch: { identity: { immediate: true, handler() {
      const id = siteAccount.user?.id || '';
      if (owner && owner !== id) { generation++; owner = ''; pending = null; state.busy = false; state.storage = 'local'; committed = readLocal(); apply(committed); try { localStorage.removeItem(marker); } catch {} state.error = '云端账号已切换，已恢复本地设置'; }
      let saved = ''; try { saved = localStorage.getItem(marker) || ''; } catch { return; }
      if (siteAccount.ready && saved && id === saved && !restoring && state.storage === 'local' && !state.busy) { restoring = true; void select('cloud').finally(() => { restoring = false; }); }
    } } } });
    void restoreSiteAccount().catch(() => {});
  });
  return { state, select, changed };
}
